import React, {
  ChangeEvent,
  ReactElement,
  cloneElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { RadioOrSelectOption } from '../../../../types/common.types';

import { getClassNames } from '../../../../utils/util';

import { ReactComponent as Icon } from '../../../../resources/icons/dropdown-arrow.svg';

import './dropdown.scss';

type Props = {
  label?: string;
  name?: string;
  options: RadioOrSelectOption[];
  value?: RadioOrSelectOption;
  onChange: (option: RadioOrSelectOption) => void;
  disabled?: boolean;
  inline?: boolean;
  placeholder?: string;
  renderOption?: (option: RadioOrSelectOption) => ReactElement;
};

const Dropdown = ({
  label,
  name,
  options,
  value,
  onChange,
  disabled = false,
  inline = false,
  placeholder,
  renderOption
}: Props) => {
  const [filter, setFilter] = useState('');
  const [showOptions, setShowOptions] = useState(false);

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const handleOutsideClick = useCallback((event: Event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setShowOptions(false);
      setFilter('');
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);

    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [handleOutsideClick]);

  const filteredOptions = useMemo(() => {
    if (!filter) return options;

    const lowerCaseFilter = filter.toLowerCase();

    return options.filter((option) => option.label.toLowerCase().includes(lowerCaseFilter));
  }, [options, filter]);

  const handleTextInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFilter(event.target.value);
  };

  const handleIconClick = () => {
    if (disabled) return;

    setShowOptions(!showOptions);
  };

  const handleOptionClick = (option: RadioOrSelectOption) => {
    onChange(option);
    setFilter('');
    setShowOptions(false);
  };

  const determinePlaceholder = () => {
    if (filter) return filter;

    if (value?.label) return value.label;

    return placeholder ?? '';
  };

  const renderOptions = () => {
    if (filteredOptions.length === 0) {
      return <span className="dropdown__option dropdown__option--empty">No Options</span>;
    }

    if (renderOption) {
      return filteredOptions.map((option) => {
        const optionElement = renderOption(option);

        const className = [
          getClassNames('dropdown__option', { selected: option.value === value?.value }),
          optionElement.props.className
        ]
          .filter(Boolean)
          .join(' ');

        return cloneElement(optionElement, {
          key: option.value,
          className,
          onClick: () => handleOptionClick(option)
        });
      });
    }

    return filteredOptions.map((option) => {
      return (
        <li
          className={getClassNames('dropdown__option', { selected: option.value === value?.value })}
          key={option.value}
          onClick={() => handleOptionClick(option)}
        >
          {option.label}
        </li>
      );
    });
  };

  return (
    <div className={getClassNames('dropdown', { inline })}>
      {!inline && (
        <div className="dropdown__label-container">
          <label htmlFor={name}>{label}</label>
        </div>
      )}
      <div className={getClassNames('dropdown__container', { disabled })} ref={dropdownRef}>
        <div className={getClassNames('dropdown__input-container', { inline, disabled })}>
          <input
            className={getClassNames('dropdown__input', { inline })}
            id={name}
            name={name}
            value={filter}
            placeholder={determinePlaceholder()}
            onChange={handleTextInputChange}
            disabled={disabled}
            onMouseDown={() => setShowOptions(true)}
            autoComplete="off"
          />

          <button
            className={getClassNames('dropdown__icon', { inverted: showOptions, disabled })}
            onClick={handleIconClick}
          >
            <Icon />
          </button>
        </div>

        {showOptions && <ul className={getClassNames('dropdown__options', { inline })}>{renderOptions()}</ul>}
      </div>
    </div>
  );
};

export default Dropdown;
